import "./styles.css";
import { ATTRACT_BOARD } from "./attract";
import { GameRuntime, type RuntimeEvent } from "./gameRuntime";
import { colorComboText, formatNumber, formatTemplate, languageOptions, resolveLanguage, type LocalizedText, type StaticTextKey } from "./i18n";
import { mountApp } from "./view";
import { defaultAchievements, type Achievements, type GameSettings, type SceneState } from "../core/model";
import { drawPiecePreview } from "../render/spriteAtlas";
import { BOARD_FRAMED_ASPECT, renderBoard } from "../render/boardRenderer";
import { formatScore, percentForLevelProgress } from "../render/uiRenderer";
import { MAX_NAME_LENGTH, createDefaultRanking, isNameTooLong, nameCharacterCount, rankForScore, sanitizeName } from "../core/ranking";
import { commandFromKeyboardEvent, commandsFromGamepad, type GameCommand } from "../platform/input";
import { AudioEngine } from "../platform/audio";
import {
  loadAchievements,
  loadLastName,
  loadRanking,
  loadSettings,
  saveAchievements,
  saveLastName,
  saveRanking,
  saveSettings
} from "../platform/storage";
import { PwaInstallController, registerServiceWorker } from "../platform/pwa";
import { installAnalytics, trackEvent } from "../platform/analytics";

installAnalytics();

const runtime = new GameRuntime({
  ranking: loadRanking(),
  settings: loadSettings(),
  achievements: loadAchievements()
});
const audio = new AudioEngine();

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) {
  throw new Error("#app was not found");
}

const appRoot = app;
const ui = mountApp(appRoot);
const pwaInstall = new PwaInstallController((outcome) => {
  trackEvent("pwa_install_prompt", { outcome });
});

const repeatCommands = new Map<GameCommand, number>();
const commandQueue: GameCommand[] = [];
const HORIZONTAL_REPEAT_DELAY_MS = 165;
const HORIZONTAL_REPEAT_INTERVAL_MS = 72;
const MAX_REFRESH_RATE_HZ = 60;
const MIN_FRAME_INTERVAL_MS = 1000 / MAX_REFRESH_RATE_HZ;
const SWIPE_THRESHOLD_PX = 30;
const TAP_DEADZONE_PX = 16;
let lastTime = performance.now();
let appActive = isPageActive();
let loopFrameId = 0;
let previousState = runtime.state;
let previousLevel = runtime.level;
let previousScore = runtime.score;
let previousAchievements = JSON.stringify(runtime.achievements);
let previousStaticLanguage = "";
let previousRankingSignature = "";
let previousNextPreviewKey = "";
let lastName = loadLastName();
let lastGamepadFire = 0;
let lastGamepadPressed = new Set<GameCommand>();
let modalReturnState: SceneState = "opening";
let toastTimer = 0;
let gesturePointer:
  | {
      id: number;
      x: number;
      y: number;
      time: number;
    }
  | null = null;

registerServiceWorker();
syncStaticText(true);
syncAudio();

function currentText(): LocalizedText {
  return resolveLanguage(runtime.settings.language).text;
}

function syncStaticText(force = false): void {
  const resolved = resolveLanguage(runtime.settings.language);
  const text = resolved.text;
  if (!force && previousStaticLanguage === resolved.language) {
    return;
  }
  previousStaticLanguage = resolved.language;
  if (document.documentElement.lang !== resolved.language) {
    document.documentElement.lang = resolved.language;
  }
  if (document.documentElement.dir !== text.dir) {
    document.documentElement.dir = text.dir;
  }

  for (const element of appRoot.querySelectorAll<HTMLElement>("[data-i18n]")) {
    const key = element.dataset.i18n as StaticTextKey;
    setText(element, text.static[key] ?? element.textContent ?? "");
  }
  for (const element of appRoot.querySelectorAll<HTMLElement>("[data-i18n-title]")) {
    const key = element.dataset.i18nTitle as StaticTextKey;
    const title = text.static[key] ?? element.title;
    if (element.title !== title) {
      element.title = title;
    }
  }
  for (const element of appRoot.querySelectorAll<HTMLElement>("[data-i18n-aria]")) {
    const key = element.dataset.i18nAria as StaticTextKey;
    const label = text.static[key] ?? element.getAttribute("aria-label") ?? "";
    if (element.getAttribute("aria-label") !== label) {
      element.setAttribute("aria-label", label);
    }
  }
}

function runCommand(command: GameCommand): boolean {
  audio.unlock();
  let consumed = false;
  if (command === "start") {
    consumed = runtime.command("start");
  } else if (command === "settings") {
    openSettings();
    consumed = true;
  } else {
    consumed = runtime.command(command);
  }
  if (!consumed) {
    return false;
  }
  handleRuntimeEvents(runtime.drainEvents());
  saveRanking(runtime.ranking);
  syncUi();
  return true;
}

function normalizeCommand(command: GameCommand): GameCommand {
  if (runtime.state === "opening" || runtime.state === "ranking" || runtime.state === "gameOver") {
    if (command === "confirm" || command === "hardDrop" || command === "rotateRight" || command === "rotateLeft" || command === "softDrop") {
      return "start";
    }
  }
  if (runtime.state === "paused") {
    if (command === "start" || command === "hardDrop" || command === "rotateRight" || command === "rotateLeft" || command === "softDrop") {
      return "confirm";
    }
  }
  return command;
}

function isHorizontalCommand(command: GameCommand): command is "left" | "right" {
  return command === "left" || command === "right";
}

function executeCommand(command: GameCommand): void {
  if (command === "help") {
    openHelp();
    return;
  }
  enqueueCommand(command);
}

function enqueueCommand(command: GameCommand): void {
  commandQueue.push(command);
  processCommandQueue();
}

function processCommandQueue(): void {
  while (commandQueue.length > 0) {
    const command = normalizeCommand(commandQueue[0]);
    if (!runCommand(command)) {
      break;
    }
    commandQueue.shift();
  }
}

function pressHeldCommand(command: GameCommand): void {
  const normalized = normalizeCommand(command);
  if (isHorizontalCommand(normalized)) {
    repeatCommands.set(normalized, performance.now() + HORIZONTAL_REPEAT_DELAY_MS);
  }
  executeCommand(command);
}

function releaseHeldCommand(command: GameCommand): void {
  const normalized = normalizeCommand(command);
  repeatCommands.delete(normalized);
}

function canStartGestureInput(): boolean {
  return runtime.state === "opening" || runtime.state === "playing" || runtime.state === "paused" || runtime.state === "gameOver";
}

function isGestureBlockedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return true;
  }
  return Boolean(
    target.closest(
      "dialog, .topbar, .touch-controls, .center-prompt, button, a, input, select, textarea, label"
    )
  );
}

function commandFromGesture(dx: number, dy: number, distance: number): GameCommand | null {
  if (distance <= TAP_DEADZONE_PX) {
    if (runtime.state === "paused") {
      return "confirm";
    }
    if (runtime.state === "opening" || runtime.state === "gameOver") {
      return "start";
    }
    return runtime.state === "playing" ? "rotateRight" : null;
  }

  if (runtime.state !== "playing" || distance < SWIPE_THRESHOLD_PX) {
    return null;
  }

  if (Math.abs(dx) > Math.abs(dy)) {
    return dx < 0 ? "left" : "right";
  }
  return dy > 0 ? "hardDrop" : "rotateRight";
}

function processCommandRepeats(now: number): void {
  for (const [command, nextFire] of repeatCommands) {
    if (now >= nextFire) {
      enqueueCommand(command);
      repeatCommands.set(command, now + HORIZONTAL_REPEAT_INTERVAL_MS);
    }
  }
}

function isTextInputTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}

function handleRuntimeEvents(events: RuntimeEvent[]): void {
  const text = currentText();
  for (const event of events) {
    if (event === "start") {
      trackEvent("game_start", {
        level: runtime.level,
        score: runtime.score,
        type_count: typeCountForLevel(runtime.level)
      });
    }
    if (event === "gameOver") {
      audio.restartBgm("opening");
      const rank = rankForScore(runtime.score, runtime.ranking);
      trackEvent("game_over", {
        score: runtime.score,
        level: runtime.level,
        chain: runtime.chain,
        erased_total: runtime.erasedTotal,
        ranked: rank <= runtime.ranking.length,
        rank,
        reason: runtime.gameOverReason || "top_out"
      });
    }
    if (event === "move" || event === "rotate") {
      audio.playSe("move");
    } else if (event === "hit") {
      audio.playSe(event);
    } else if (event === "pause") {
      audio.playSe("pause");
      trackEvent(runtime.state === "paused" ? "game_pause" : "game_resume", {
        score: runtime.score,
        level: runtime.level
      });
    } else if (event === "erase" || event === "gameOver") {
      audio.playSe(event);
    }
    if (event === "erase") {
      trackEvent("characters_erased", {
        score: runtime.score,
        score_add: runtime.lastScoreAdd,
        level: runtime.level,
        chain: runtime.chain,
        erased_total: runtime.erasedTotal
      });
      showToast(formatTemplate(text.toast.chain, { value: Math.max(1, runtime.chain) }));
    }
  }
}

function typeCountForLevel(level: number): number {
  return level < 11 ? 4 : level < 21 ? 5 : 6;
}

function playButtonBeep(): void {
  void audio.unlock();
  audio.playSe("button");
}

function syncAudio(): void {
  audio.setEnabled({
    bgm: runtime.settings.bgm,
    se: runtime.settings.se,
    bgmVolume: runtime.settings.bgmVolume,
    seVolume: runtime.settings.seVolume
  });
  audio.setBgmMuted(!isPageActive());
  if (!isPageActive()) {
    return;
  }
  if (runtime.state === "help" || runtime.state === "settings") {
    return;
  }
  if (runtime.state === "playing" || runtime.state === "dropping" || runtime.state === "resolvingErase" || runtime.state === "applyingGravity") {
    audio.startBgm("play");
  } else if (runtime.state === "opening" || runtime.state === "ranking" || runtime.state === "gameOverSequence" || runtime.state === "gameOver" || runtime.state === "nameEntry") {
    audio.startBgm("opening");
  } else {
    audio.stopBgm();
  }
}

function syncRanking(force = false): void {
  const text = currentText();
  const signature = `${text.locale}|${runtime.ranking.map((entry) => `${entry.name}:${entry.score}`).join("|")}`;
  if (!force && previousRankingSignature === signature) {
    return;
  }
  previousRankingSignature = signature;
  ui.rankingList.replaceChildren(
    ...runtime.ranking.map((entry, index) => makeRankingRow(entry.name, entry.score, index, text))
  );
}

function drawNext(): void {
  const rect = ui.nextCanvas.getBoundingClientRect();
  const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
  const width = Math.max(1, Math.floor(rect.width * dpr));
  const height = Math.max(1, Math.floor(rect.height * dpr));
  const pair = runtime.nextPair ?? { upperType: 1, lowerType: 2 };
  const key = `${width}x${height}:${pair.upperType},${pair.lowerType}`;
  if (previousNextPreviewKey === key) {
    return;
  }
  previousNextPreviewKey = key;
  if (ui.nextCanvas.width !== width || ui.nextCanvas.height !== height) {
    ui.nextCanvas.width = width;
    ui.nextCanvas.height = height;
  }
  const ctx = ui.nextCanvas.getContext("2d");
  if (!ctx) {
    return;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  drawPiecePreview(ctx, pair.upperType, pair.lowerType, rect.width, rect.height, false);
}

function syncBoardCanvasSize(): void {
  const layoutRect = ui.gameLayout.getBoundingClientRect();
  if (layoutRect.width <= 0 || layoutRect.height <= 0) {
    return;
  }
  const styles = getComputedStyle(ui.gameLayout);
  const columnGap = cssPx(styles.columnGap) || cssPx(styles.gap);
  const sideMin = cssPx(styles.getPropertyValue("--side-min-width")) || 220;
  const maxWidth = Math.max(1, layoutRect.width - sideMin - columnGap);
  const maxHeight = Math.max(1, layoutRect.height);
  const width = Math.max(1, Math.floor(Math.min(maxWidth, maxHeight * BOARD_FRAMED_ASPECT)));
  const height = Math.max(1, Math.floor(width / BOARD_FRAMED_ASPECT));
  setStyleProperty(appRoot, "--board-canvas-width", `${width}px`);
  setStyleProperty(appRoot, "--board-canvas-height", `${height}px`);
}

function cssPx(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function setText(element: Element, value: string): void {
  if (element.textContent !== value) {
    element.textContent = value;
  }
}

function setHidden(element: HTMLElement, hidden: boolean): void {
  if (element.hidden !== hidden) {
    element.hidden = hidden;
  }
}

function setStyleProperty(element: HTMLElement, property: string, value: string): void {
  if (element.style.getPropertyValue(property) !== value) {
    element.style.setProperty(property, value);
  }
}

function syncUi(): void {
  const text = currentText();
  syncStaticText();
  syncBoardCanvasSize();
  const snapshot = runtime.snapshot();
  setStyleProperty(appRoot, "touch-action", canStartGestureInput() && !ui.modal.open ? "none" : "auto");
  const isAttract = snapshot.showAttractBoard;
  const boardMetrics = renderBoard(ui.boardCanvas, {
    board: isAttract ? ATTRACT_BOARD : snapshot.board,
    currentPair: isAttract ? null : snapshot.currentPair,
    pairAnimation: isAttract ? null : snapshot.pairAnimation,
    gravityAnimation: isAttract ? null : snapshot.gravityAnimation,
    landingAnimation: isAttract ? null : snapshot.landingAnimation,
    eraseAnimation: isAttract ? null : snapshot.eraseAnimation,
    eraseTextAnimations: isAttract ? [] : snapshot.eraseTextAnimations,
    gameOverAnimation: isAttract ? null : snapshot.gameOverAnimation,
    highContrast: false,
    time: performance.now(),
    text: {
      gameOver: text.canvas.gameOver,
      chain: (chain) => formatTemplate(text.canvas.chain, { value: chain }),
      colorCombo: (colorCount) => colorComboText(text, colorCount)
    }
  });
  setStyleProperty(appRoot, "--board-x", `${boardMetrics.boardX}px`);
  setStyleProperty(appRoot, "--board-y", `${boardMetrics.boardY}px`);
  setStyleProperty(appRoot, "--board-width", `${boardMetrics.boardWidth}px`);
  setStyleProperty(appRoot, "--board-height", `${boardMetrics.boardHeight}px`);
  setStyleProperty(appRoot, "--next-preview-max-width", `${Math.max(1, boardMetrics.cell / 0.7)}px`);
  setStyleProperty(ui.centerPrompt, "--prompt-x", `${boardMetrics.boardX + boardMetrics.boardWidth / 2}px`);
  setStyleProperty(ui.centerPrompt, "--prompt-y", `${boardMetrics.boardY + boardMetrics.boardHeight / 2}px`);
  setStyleProperty(ui.centerPrompt, "--prompt-max-width", `${Math.max(220, boardMetrics.boardWidth * 0.96)}px`);
  setStyleProperty(ui.centerPrompt, "--prompt-logo-size", `${Math.max(56, Math.min(104, boardMetrics.boardWidth * 0.26))}px`);
  setStyleProperty(ui.statusToast, "--toast-x", `${boardMetrics.boardX + boardMetrics.boardWidth / 2}px`);
  setStyleProperty(ui.statusToast, "--toast-y", `${boardMetrics.boardY + Math.max(10, boardMetrics.cell * 0.42)}px`);
  drawNext();
  setText(ui.scoreValue, formatScore(snapshot.score, text.locale));
  setText(ui.levelValue, String(snapshot.level));
  setText(ui.chainValue, String(snapshot.chain));
  setText(ui.typeValue, String(typeCountForLevel(snapshot.level)));
  setStyleProperty(ui.levelProgress, "width", percentForLevelProgress(snapshot.levelProgress));
  setHidden(ui.touchControls, !snapshot.settings.touchControls);
  setHidden(ui.centerPrompt, snapshot.state !== "opening" && snapshot.state !== "paused" && snapshot.state !== "gameOver");
  setHidden(ui.pauseButton, !isPauseButtonVisibleState(snapshot.state));
  const hideRecords = isActiveGameState(snapshot.state);
  setHidden(ui.rankingButton, hideRecords);
  setHidden(ui.achievementsButton, hideRecords);
  syncPauseButton(snapshot.state, text);

  if (!ui.centerPrompt.hidden) {
    const title = ui.centerPrompt.querySelector(".logo-word");
    const subtitle = ui.centerPrompt.querySelector(".prompt-action");
    if (title && subtitle) {
      setCenterPromptTitle(title, snapshot.state, text);
      setText(subtitle, snapshot.state === "paused" ? text.prompt.resume : text.prompt.start);
    }
  }

  if (snapshot.level !== previousLevel) {
    if (snapshot.level > previousLevel) {
      trackEvent("level_up", {
        level: snapshot.level,
        score: snapshot.score,
        type_count: typeCountForLevel(snapshot.level)
      });
    }
    showToast(formatTemplate(text.toast.level, { value: snapshot.level }));
  } else if (snapshot.score !== previousScore && snapshot.lastScoreAdd > 0) {
    showToast(`+${formatScore(snapshot.lastScoreAdd, text.locale)}`);
  }

  if (shouldPlayCurtainTransition(previousState, snapshot.state)) {
    ui.screenCurtain.classList.remove("play-transition");
    void ui.screenCurtain.offsetWidth;
    ui.screenCurtain.classList.add("play-transition");
  }

  syncRanking();
  syncAchievements();
  syncAudio();
  syncModal();
  previousState = runtime.state;
  previousLevel = snapshot.level;
  previousScore = snapshot.score;
}

function setCenterPromptTitle(title: Element, state: SceneState, text: LocalizedText): void {
  if (!(title instanceof HTMLElement)) {
    return;
  }
  if (state === "paused") {
    if (title.dataset.mode !== "plain") {
      title.dataset.mode = "plain";
      title.removeAttribute("aria-label");
      title.replaceChildren(document.createTextNode(text.prompt.paused));
    }
    return;
  }

  if (title.dataset.mode === "friel") {
    return;
  }
  title.dataset.mode = "friel";
  title.setAttribute("aria-label", "FRIEL");
  title.replaceChildren(
    ...["f", "r", "i", "e", "l"].map((letter) => {
      const span = document.createElement("span");
      span.textContent = letter;
      return span;
    })
  );
}

function syncPauseButton(state: SceneState, text: LocalizedText): void {
  const isPaused = state === "paused";
  const label = isPaused ? text.prompt.resume : text.prompt.paused;
  setText(ui.pauseButton, isPaused ? "\u25b6" : "\u275a\u275a");
  if (ui.pauseButton.title !== label) {
    ui.pauseButton.title = label;
  }
  if (ui.pauseButton.getAttribute("aria-label") !== label) {
    ui.pauseButton.setAttribute("aria-label", label);
  }
}

function shouldPlayCurtainTransition(from: SceneState, to: SceneState): boolean {
  if (from === to) {
    return false;
  }
  const menuStates = new Set<SceneState>(["opening", "ranking", "help", "settings", "nameEntry", "gameOver"]);
  return menuStates.has(from) || menuStates.has(to);
}

function isActiveGameState(state: SceneState): boolean {
  return (
    state === "playing" ||
    state === "dropping" ||
    state === "paused" ||
    state === "resolvingErase" ||
    state === "applyingGravity" ||
    state === "gameOverSequence"
  );
}

function isPauseButtonVisibleState(state: SceneState): boolean {
  return state === "playing" || state === "dropping" || state === "paused" || state === "resolvingErase" || state === "applyingGravity";
}

function syncAchievements(): void {
  const serialized = JSON.stringify(runtime.achievements);
  if (serialized !== previousAchievements) {
    previousAchievements = serialized;
    saveAchievements(runtime.achievements);
  }
}

function showToast(message: string): void {
  ui.statusToast.textContent = message;
  ui.statusToast.classList.remove("show");
  void ui.statusToast.offsetWidth;
  ui.statusToast.classList.add("show");
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => ui.statusToast.classList.remove("show"), 900);
}

function syncModal(): void {
  const text = currentText();
  if (runtime.state === "nameEntry") {
    const rank = rankForScore(runtime.score, runtime.ranking);
    if (previousState !== "nameEntry") {
      trackEvent("high_score_prompt", {
        score: runtime.score,
        rank
      });
    }
    clearModalExtraAction();
    ui.modalTitle.textContent = text.modal.nameEntry;
    ui.modalBody.textContent = `${text.nameEntry.score} ${formatScore(runtime.score, text.locale)}\n${text.nameEntry.rank} ${rank}`;
    ui.nameInput.hidden = false;
    ui.nameWarning.hidden = false;
    ui.modalCancel.hidden = false;
    ui.modalConfirm.hidden = false;
    if (!ui.modal.open) {
      ui.modal.showModal();
      ui.nameInput.value = lastName;
      ui.nameInput.select();
      ui.nameInput.focus();
    }
    syncNameEntryValidation();
    return;
  }

  if (previousState === "nameEntry" && ui.modal.open) {
    ui.modal.close();
  }
  resetNameEntryValidation();
}

function syncNameEntryValidation(): boolean {
  const count = nameCharacterCount(ui.nameInput.value);
  const tooLong = isNameTooLong(ui.nameInput.value);
  ui.nameWarning.textContent = tooLong ? nameEntryWarningText(currentText(), count) : "";
  ui.nameWarning.hidden = !tooLong;
  ui.nameInput.setAttribute("aria-invalid", String(tooLong));
  ui.modalConfirm.disabled = tooLong;
  return !tooLong;
}

function resetNameEntryValidation(): void {
  ui.nameWarning.textContent = "";
  ui.nameWarning.hidden = true;
  ui.nameInput.removeAttribute("aria-invalid");
  ui.modalConfirm.disabled = false;
}

function nameEntryWarningText(text: LocalizedText, count: number): string {
  if (text.locale === "ja-JP") {
    return `名前は${MAX_NAME_LENGTH}文字以内にしてください。現在${count}文字です。`;
  }
  return `Use ${MAX_NAME_LENGTH} characters or fewer. Current length: ${count}.`;
}

function openSettings(): void {
  const text = currentText();
  modalReturnState = runtime.state;
  trackEvent("menu_open", {
    menu_name: "settings",
    from_state: modalReturnState
  });
  clearModalExtraAction();
  ui.modalTitle.textContent = text.modal.settings;
  ui.modalBody.replaceChildren(document.querySelector<HTMLTemplateElement>("#settingsTemplate")!.content.cloneNode(true));
  ui.nameInput.hidden = true;
  resetNameEntryValidation();
  ui.modalCancel.hidden = true;
  ui.modalConfirm.hidden = false;
  syncStaticText(true);

  const bgmToggle = ui.modal.querySelector<HTMLInputElement>("#bgmToggle")!;
  const bgmVolume = ui.modal.querySelector<HTMLInputElement>("#bgmVolume")!;
  const bgmVolumeValue = ui.modal.querySelector<HTMLOutputElement>("#bgmVolumeValue")!;
  const seToggle = ui.modal.querySelector<HTMLInputElement>("#seToggle")!;
  const seVolume = ui.modal.querySelector<HTMLInputElement>("#seVolume")!;
  const seVolumeValue = ui.modal.querySelector<HTMLOutputElement>("#seVolumeValue")!;
  const touchToggle = ui.modal.querySelector<HTMLInputElement>("#touchToggle")!;
  const languageSelect = ui.modal.querySelector<HTMLSelectElement>("#languageSelect")!;
  const pwaInstallButton = ui.modal.querySelector<HTMLButtonElement>("#pwaInstallButton");
  pwaInstall.attachButton(pwaInstallButton);
  populateLanguageSelect(languageSelect);
  bgmToggle.checked = runtime.settings.bgm;
  bgmVolume.value = String(runtime.settings.bgmVolume);
  bgmVolumeValue.value = String(runtime.settings.bgmVolume);
  seToggle.checked = runtime.settings.se;
  seVolume.value = String(runtime.settings.seVolume);
  seVolumeValue.value = String(runtime.settings.seVolume);
  touchToggle.checked = runtime.settings.touchControls;
  languageSelect.value = runtime.settings.language;

  const updateSettings = () => {
    runtime.settings = normalizeSettings({
      bgm: bgmToggle.checked,
      se: seToggle.checked,
      bgmVolume: Number(bgmVolume.value),
      seVolume: Number(seVolume.value),
      touchControls: touchToggle.checked,
      language: languageSelect.value as GameSettings["language"]
    });
    bgmVolumeValue.value = String(runtime.settings.bgmVolume);
    seVolumeValue.value = String(runtime.settings.seVolume);
    populateLanguageSelect(languageSelect);
    languageSelect.value = runtime.settings.language;
    ui.modalTitle.textContent = currentText().modal.settings;
    saveSettings(runtime.settings);
    syncUi();
  };

  for (const input of [bgmToggle, bgmVolume, seToggle, seVolume, touchToggle, languageSelect]) {
    input.addEventListener("input", updateSettings);
    input.addEventListener("change", updateSettings);
  }
  bgmToggle.addEventListener("change", () => trackSettingChange("bgm", bgmToggle.checked));
  bgmVolume.addEventListener("change", () => trackSettingChange("bgm_volume", Number(bgmVolume.value)));
  seToggle.addEventListener("change", () => trackSettingChange("se", seToggle.checked));
  seVolume.addEventListener("change", () => trackSettingChange("se_volume", Number(seVolume.value)));
  touchToggle.addEventListener("change", () => trackSettingChange("touch_controls", touchToggle.checked));
  languageSelect.addEventListener("change", () => trackSettingChange("language", languageSelect.value));

  runtime.state = "settings";
  ui.modal.showModal();
}

function trackSettingChange(settingName: string, value: string | number | boolean): void {
  trackEvent("settings_change", {
    setting_name: settingName,
    setting_value: String(value)
  });
}

function populateLanguageSelect(select: HTMLSelectElement): void {
  const resolved = resolveLanguage(runtime.settings.language);
  select.replaceChildren(
    ...languageOptions(resolved).map((language) => {
      const option = document.createElement("option");
      option.value = language.value;
      option.textContent = language.label;
      return option;
    })
  );
}

function normalizeSettings(settings: GameSettings): GameSettings {
  return {
    bgm: settings.bgm,
    se: settings.se,
    bgmVolume: Math.max(0, Math.min(100, Math.round(settings.bgmVolume / 10) * 10)),
    seVolume: Math.max(0, Math.min(100, Math.round(settings.seVolume / 10) * 10)),
    touchControls: settings.touchControls,
    language: settings.language
  };
}

function openHelp(): void {
  const text = currentText();
  modalReturnState = runtime.state;
  trackEvent("menu_open", {
    menu_name: "help",
    from_state: modalReturnState
  });
  clearModalExtraAction();
  ui.modalTitle.textContent = text.modal.help;
  ui.modalBody.replaceChildren(makeHelpPanel(text));
  ui.nameInput.hidden = true;
  resetNameEntryValidation();
  ui.modalCancel.hidden = true;
  ui.modalConfirm.hidden = false;
  runtime.state = "help";
  ui.modal.showModal();
}

function makeHelpPanel(text: LocalizedText): HTMLElement {
  const panel = document.createElement("div");
  panel.className = "help-panel";
  panel.append(...text.helpSections.map((section) => makeHelpSection(section.title, section.lines)), makePrivacyPolicySection());
  return panel;
}

function makeHelpSection(title: string, lines: string[]): HTMLElement {
  const section = document.createElement("section");
  section.className = "help-section";
  const heading = document.createElement("h3");
  const list = document.createElement("ul");
  heading.textContent = title;
  for (const line of lines) {
    const item = document.createElement("li");
    item.textContent = line;
    list.append(item);
  }
  section.append(heading, list);
  return section;
}

function makePrivacyPolicySection(): HTMLElement {
  const section = document.createElement("section");
  section.className = "help-section privacy-section";
  const heading = document.createElement("h3");
  const paragraph = document.createElement("p");
  const link = document.createElement("a");
  heading.textContent = "Privacy Policy";
  link.href = "/privacy.html";
  link.textContent = "Privacy Policy";
  paragraph.append(link);
  section.append(heading, paragraph);
  return section;
}

function openRankingModal(): void {
  const text = currentText();
  modalReturnState = runtime.state;
  trackEvent("menu_open", {
    menu_name: "ranking",
    from_state: modalReturnState
  });
  clearModalExtraAction();
  ui.modalTitle.textContent = text.modal.ranking;
  ui.modalBody.replaceChildren(makeRankingList(text));
  ui.nameInput.hidden = true;
  resetNameEntryValidation();
  ui.modalCancel.hidden = false;
  ui.modalConfirm.hidden = false;
  ui.modal.showModal();
}

function openAchievementsModal(): void {
  const text = currentText();
  modalReturnState = runtime.state;
  trackEvent("menu_open", {
    menu_name: "achievements",
    from_state: modalReturnState
  });
  showAchievementsResetAction();
  ui.modalTitle.textContent = text.modal.achievements;
  ui.modalBody.replaceChildren(makeAchievementsPanel(runtime.achievements, text));
  ui.nameInput.hidden = true;
  resetNameEntryValidation();
  ui.modalCancel.hidden = false;
  ui.modalConfirm.hidden = false;
  ui.modal.showModal();
}

function makeAchievementsPanel(achievements: Achievements, text: LocalizedText): HTMLElement {
  const panel = document.createElement("div");
  panel.className = "achievements-panel";
  const share = makeAchievementsSharePanel(achievements, text);
  panel.append(makeAchievementsList(achievements, text), share);
  return panel;
}

function makeAchievementsSharePanel(achievements: Achievements, uiText: LocalizedText): HTMLElement {
  const text = achievementShareText(achievements, uiText);
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent("https://friel.frievegames.com/");
  const panel = document.createElement("div");
  const label = document.createElement("span");
  const x = makeIconLink(uiText.share.x, `https://twitter.com/intent/tweet?text=${encodedText}`, makeXIcon());
  const facebook = makeIconLink(uiText.share.facebook, `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, makeFacebookIcon());
  const clipboard = makeIconButton(uiText.share.clipboard, makeClipboardIcon());
  panel.className = "share-panel";
  label.className = "share-label";
  label.textContent = uiText.share.label;
  x.addEventListener("click", () => trackShare("x"));
  facebook.addEventListener("click", () => trackShare("facebook"));
  clipboard.addEventListener("click", async () => {
    if (await copyShareText(text)) {
      trackShare("clipboard");
      showToast(currentText().toast.copied);
      return;
    }
    showToast(currentText().toast.copyFailed);
  });
  panel.append(label, x, facebook, clipboard);
  return panel;
}

function trackShare(method: string): void {
  trackEvent("share", {
    method,
    content_type: "achievements"
  });
}

function clearModalExtraAction(): void {
  ui.modalExtraAction.hidden = true;
  ui.modalExtraAction.textContent = "";
  ui.modalExtraAction.onclick = null;
}

function showAchievementsResetAction(): void {
  const text = currentText();
  ui.modalExtraAction.hidden = false;
  ui.modalExtraAction.textContent = text.achievements.resetRecords;
  ui.modalExtraAction.onclick = resetRecords;
}

function resetRecords(): void {
  const text = currentText();
  if (!window.confirm(text.achievements.resetConfirm)) {
    return;
  }
  runtime.achievements = defaultAchievements();
  runtime.ranking = createDefaultRanking();
  previousAchievements = JSON.stringify(runtime.achievements);
  saveAchievements(runtime.achievements);
  saveRanking(runtime.ranking);
  syncRanking();
  trackEvent("records_reset");
  showToast(text.toast.recordsReset);
  openAchievementsModal();
}

function makeIconLink(label: string, href: string, icon: SVGSVGElement): HTMLAnchorElement {
  const link = document.createElement("a");
  link.className = "text-button share-button";
  link.href = href;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.title = label;
  link.setAttribute("aria-label", label);
  link.append(icon);
  return link;
}

function makeIconButton(label: string, icon: SVGSVGElement): HTMLButtonElement {
  const button = document.createElement("button");
  button.className = "text-button share-button";
  button.type = "button";
  button.title = label;
  button.setAttribute("aria-label", label);
  button.append(icon);
  return button;
}

function makeXIcon(): SVGSVGElement {
  const svg = makeSvgIcon();
  svg.append(
    makeSvgPath(
      "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817-5.966 6.817H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z",
      true
    )
  );
  return svg;
}

function makeFacebookIcon(): SVGSVGElement {
  const svg = makeSvgIcon();
  svg.append(makeSvgPath("M14.2 8H16V5h-2.4c-2.6 0-4.1 1.5-4.1 4v2H7v3h2.5v7h3.2v-7h2.6l.5-3h-3.1V9.3c0-.9.4-1.3 1.5-1.3Z", true));
  return svg;
}

function makeClipboardIcon(): SVGSVGElement {
  const svg = makeSvgIcon();
  svg.append(makeSvgPath("M9 5h6l1 2h2v13H6V7h2l1-2Z", true));
  svg.append(makeSvgPath("M9 11h6M9 15h6", false));
  return svg;
}

function makeSvgIcon(): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  return svg;
}

function makeSvgPath(d: string, filled: boolean): SVGPathElement {
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", d);
  if (filled) {
    path.setAttribute("fill", "currentColor");
    return path;
  }
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "2.4");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  return path;
}

function achievementShareText(achievements: Achievements, text: LocalizedText): string {
  return [
    text.share.resultsTitle,
    `${text.achievements.highScore}: ${formatScore(achievements.highScore, text.locale)}`,
    `${text.achievements.maxLevel}: ${formatNumber(achievements.maxLevel, text)}`,
    `${text.achievements.maxChain}: ${formatNumber(achievements.maxChain, text)}`,
    `${text.achievements.maxSimultaneousErase}: ${formatNumber(achievements.maxSimultaneousErase, text)}`,
    `${text.share.play}: https://friel.frievegames.com/`
  ].join("\n");
}

async function copyShareText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the textarea path for browsers that restrict Clipboard API.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  document.body.append(textarea);
  textarea.select();
  try {
    return document.execCommand("copy");
  } finally {
    textarea.remove();
  }
}

function makeAchievementsList(achievements: Achievements, text: LocalizedText): HTMLElement {
  const list = document.createElement("dl");
  list.className = "achievements-list";
  const rows: Array<[string, string]> = [
    [text.achievements.highScore, formatScore(achievements.highScore, text.locale)],
    [text.achievements.maxLevel, formatNumber(achievements.maxLevel, text)],
    [text.achievements.maxChain, formatNumber(achievements.maxChain, text)],
    [text.achievements.maxSimultaneousErase, formatNumber(achievements.maxSimultaneousErase, text)]
  ];
  for (const [label, value] of rows) {
    const term = document.createElement("dt");
    const detail = document.createElement("dd");
    term.textContent = label;
    detail.textContent = value;
    list.append(term, detail);
  }
  return list;
}

function makeRankingList(text: LocalizedText): HTMLOListElement {
  const list = document.createElement("ol");
  list.className = "ranking-modal-list";
  for (const [index, entry] of runtime.ranking.entries()) {
    list.append(makeRankingRow(entry.name, entry.score, index, text));
  }
  return list;
}

function makeRankingRow(nameText: string, scoreValue: number, index: number, text: LocalizedText): HTMLLIElement {
  const rankNumber = index + 1;
  const item = document.createElement("li");
  const rank = document.createElement("span");
  const name = document.createElement("span");
  const score = document.createElement("strong");

  item.className = "ranking-row";
  item.dataset.rank = String(rankNumber);
  if (rankNumber <= 3) {
    item.classList.add(`rank-${rankNumber}`);
  }

  rank.className = "ranking-rank";
  rank.textContent = String(rankNumber).padStart(2, "0");
  name.className = "ranking-name";
  name.textContent = nameText;
  score.className = "ranking-score";
  score.textContent = formatScore(scoreValue, text.locale);
  item.append(rank, name, score);
  return item;
}

function closeModal(): void {
  if (runtime.state === "nameEntry") {
    if (!syncNameEntryValidation()) {
      ui.nameInput.focus();
      return;
    }
    submitNameEntry();
  }
  if (ui.modal.open) {
    ui.modal.close();
  }
  if (runtime.state === "settings" || runtime.state === "help") {
    runtime.state = modalReturnState === "settings" || modalReturnState === "help" ? "opening" : modalReturnState;
  }
  syncUi();
}

function submitNameEntry(): void {
  const score = runtime.score;
  const rank = rankForScore(score, runtime.ranking);
  lastName = sanitizeName(ui.nameInput.value);
  saveLastName(lastName);
  trackEvent("high_score_submit", {
    score,
    rank,
    name_length: lastName.length
  });
  runtime.submitName(lastName);
  saveRanking(runtime.ranking);
}

function gameLoop(now: number): void {
  loopFrameId = 0;
  if (!appActive) {
    return;
  }
  const dt = now - lastTime;
  if (dt < MIN_FRAME_INTERVAL_MS - 0.25) {
    scheduleGameLoop();
    return;
  }
  lastTime = now - (dt % MIN_FRAME_INTERVAL_MS);
  pollGamepad(now);
  processCommandRepeats(now);
  processCommandQueue();
  runtime.update(dt);
  processCommandQueue();
  handleRuntimeEvents(runtime.drainEvents());
  syncUi();
  scheduleGameLoop();
}

function isPageActive(): boolean {
  return !document.hidden && document.hasFocus?.() !== false;
}

function scheduleGameLoop(): void {
  if (!appActive || loopFrameId) {
    return;
  }
  loopFrameId = requestAnimationFrame(gameLoop);
}

function stopGameLoop(): void {
  if (!loopFrameId) {
    return;
  }
  cancelAnimationFrame(loopFrameId);
  loopFrameId = 0;
}

function handlePageDeactivated(): void {
  appActive = false;
  stopGameLoop();
  repeatCommands.clear();
  lastGamepadPressed.clear();
  audio.setBgmMuted(true);
  audio.suspendForLifecycle();
}

function handlePageActivated(): void {
  if (!isPageActive()) {
    return;
  }
  appActive = true;
  lastTime = performance.now();
  audio.resumeForLifecycle();
  syncAudio();
  syncUi();
  scheduleGameLoop();
}

function pollGamepad(now: number): void {
  if (typeof navigator.getGamepads !== "function") {
    return;
  }
  const pads = navigator.getGamepads();
  for (const pad of pads) {
    if (!pad) {
      continue;
    }
    const pressed = new Set(commandsFromGamepad(pad).filter((command) => command.pressed).map((command) => command.command));
    const edgeCommand = [...pressed].find((command) => !lastGamepadPressed.has(command));
    const repeatCommand = [...pressed].find((command) => isHorizontalCommand(command) && now - lastGamepadFire >= 130);
    const command = edgeCommand ?? repeatCommand;
    lastGamepadPressed = pressed;
    if (command) {
      if (isHorizontalCommand(command)) {
        lastGamepadFire = now;
      }
      enqueueCommand(command);
      break;
    }
  }
}

document.querySelector("#startButton")?.addEventListener("click", () => enqueueCommand("start"));
ui.pauseButton.addEventListener("click", (event) => {
  event.stopPropagation();
  enqueueCommand(runtime.state === "paused" ? "confirm" : "cancel");
});
ui.centerPrompt.addEventListener("click", () => enqueueCommand(runtime.state === "paused" ? "confirm" : "start"));
ui.rankingButton.addEventListener("click", openRankingModal);
ui.achievementsButton.addEventListener("click", openAchievementsModal);
document.querySelector("#helpButton")?.addEventListener("click", openHelp);
document.querySelector("#settingsButton")?.addEventListener("click", openSettings);

app.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) {
    return;
  }
  const control = target.closest("button, a.text-button");
  if (!(control instanceof HTMLElement) || control.closest(".touch-controls") || control.hidden) {
    return;
  }
  playButtonBeep();
});

ui.nameForm.addEventListener("submit", (event) => {
  event.preventDefault();
  closeModal();
});
ui.nameInput.addEventListener("input", syncNameEntryValidation);
ui.modalCancel.addEventListener("click", closeModal);
ui.modal.addEventListener("cancel", (event) => {
  event.preventDefault();
  closeModal();
});

window.addEventListener("keydown", (event) => {
  const command = commandFromKeyboardEvent(event);
  if (!command) {
    return;
  }
  if (isTextInputTarget(event.target) && command !== "cancel") {
    return;
  }
  event.preventDefault();
  if (event.repeat) {
    return;
  }
  pressHeldCommand(command);
});

window.addEventListener("keyup", (event) => {
  const command = commandFromKeyboardEvent(event);
  if (command) {
    releaseHeldCommand(command);
  }
});

for (const button of document.querySelectorAll<HTMLButtonElement>("[data-command]")) {
  const command = button.dataset.command as GameCommand;
  button.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    pressHeldCommand(command);
    button.setPointerCapture(event.pointerId);
  });
  button.addEventListener("pointerup", () => releaseHeldCommand(command));
  button.addEventListener("pointercancel", () => releaseHeldCommand(command));
}

appRoot.addEventListener("pointerdown", (event) => {
  if (event.button !== 0 && event.pointerType === "mouse") {
    return;
  }
  if (!canStartGestureInput() || isGestureBlockedTarget(event.target)) {
    return;
  }
  event.preventDefault();
  gesturePointer = {
    id: event.pointerId,
    x: event.clientX,
    y: event.clientY,
    time: performance.now()
  };
  appRoot.setPointerCapture(event.pointerId);
});

appRoot.addEventListener("pointerup", (event) => {
  if (!gesturePointer || gesturePointer.id !== event.pointerId) {
    return;
  }
  event.preventDefault();
  const dx = event.clientX - gesturePointer.x;
  const dy = event.clientY - gesturePointer.y;
  const distance = Math.hypot(dx, dy);
  gesturePointer = null;

  const command = commandFromGesture(dx, dy, distance);
  if (command) {
    executeCommand(command);
  }
});

appRoot.addEventListener("pointercancel", (event) => {
  if (gesturePointer?.id === event.pointerId) {
    gesturePointer = null;
  }
});

window.addEventListener("resize", syncUi);
document.addEventListener("visibilitychange", () => {
  if (document.hidden) {
    handlePageDeactivated();
  } else {
    handlePageActivated();
  }
});
window.addEventListener("blur", handlePageDeactivated);
window.addEventListener("focus", handlePageActivated);
window.addEventListener("pagehide", handlePageDeactivated);
window.addEventListener("pageshow", handlePageActivated);

void boot();

async function boot(): Promise<void> {
  await preloadStartupImages();
  syncUi();
  setLoadingComplete();
  scheduleGameLoop();
}

async function preloadStartupImages(): Promise<void> {
  const images = Array.from(appRoot.querySelectorAll<HTMLImageElement>("img"));
  await Promise.all(images.map(waitForImage));
}

function waitForImage(image: HTMLImageElement): Promise<void> {
  if (image.complete) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    image.addEventListener("load", () => resolve(), { once: true });
    image.addEventListener("error", () => resolve(), { once: true });
  });
}

function setLoadingComplete(): void {
  ui.appShell.classList.remove("is-loading");
  ui.loadingOverlay.hidden = true;
}
